import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Language } from '@/types';
import { getLocalizedPath } from '@/lib/utils';
import type { NavItem, MegaMenuSection, MegaMenuItem } from './Navbar';

interface MegaMenuProps {
  hoveredPath: string | null;
  setHoveredPath: (path: string | null) => void;
  hoverTimeout?: React.MutableRefObject<NodeJS.Timeout | null>;
  onMenuEnter?: () => void;
  onMenuLeave?: () => void;
  onClose?: () => void;
  navItems: NavItem[];
  lang: Language;
}

export const MegaMenu: React.FC<MegaMenuProps> = ({
  hoveredPath,
  setHoveredPath,
  hoverTimeout,
  onMenuEnter,
  onMenuLeave,
  onClose,
  navItems,
  lang
}) => {
  const activeItem = navItems.find(n => n.path === hoveredPath);
  const menu = activeItem?.megaMenu;

  const [activeHeight, setActiveHeight] = React.useState<number | 'auto'>('auto');
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setActiveHeight(entry.target.getBoundingClientRect().height);
      }
    });

    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
    };
  }, [hoveredPath]);

  return (
    <AnimatePresence>
      {hoveredPath && menu && (
        <>
          {/* Global Backdrop Blur Overlay — starts below navbar so it never intercepts cursor in navbar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed inset-0 top-14 md:top-16 z-[45] bg-black/60 dark:bg-black/80 backdrop-blur-sm pointer-events-auto"
            onMouseEnter={() => {
              if (onClose) {
                onClose();
              } else {
                if (hoverTimeout?.current) clearTimeout(hoverTimeout.current);
                setHoveredPath(null);
              }
            }}
            onClick={() => {
              if (onClose) {
                onClose();
              } else {
                if (hoverTimeout?.current) clearTimeout(hoverTimeout.current);
                setHoveredPath(null);
              }
            }}
          />

          <motion.div
            initial={{ height: 0 }}
            animate={{ height: activeHeight }}
            exit={{ height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onMouseEnter={() => {
              if (onMenuEnter) {
                onMenuEnter();
              } else if (hoverTimeout?.current) {
                clearTimeout(hoverTimeout.current);
              }
            }}
            onMouseLeave={() => {
              if (onMenuLeave) {
                onMenuLeave();
              } else {
                if (hoverTimeout?.current) clearTimeout(hoverTimeout.current);
                if (hoverTimeout) {
                  hoverTimeout.current = setTimeout(() => {
                    setHoveredPath(null);
                  }, 250);
                }
              }
            }}
            className="
              fixed top-14 md:top-16 -mt-px left-0 right-0 w-full
              bg-white dark:bg-[#05070A] border-b border-slate-200 dark:border-[#262C31] z-[60] overflow-hidden
              shadow-[0_25px_50px_-12px_rgba(0,0,0,0.85)]
              pointer-events-auto origin-top
            "
          >
            <div ref={contentRef} className="w-full relative">
              <AnimatePresence mode="popLayout">
                <motion.div
                  key={hoveredPath}
                  initial={{ opacity: 0, x: 4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -4 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="w-full max-w-[1450px] mx-auto px-6 py-10 flex gap-12 lg:gap-24"
                  dir={lang === 'ar' ? 'rtl' : 'ltr'}
                >
                  {menu.sections.map((section: MegaMenuSection, idx: number) => (
                    <div key={idx} className={`${idx === 0 ? 'flex-[1.2] max-w-lg' : 'flex-1'}`}>
                      <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-slate-500 dark:text-[#9AA5AC] mb-5">
                        {section.title}
                      </h3>

                      <div className="space-y-4">
                        {section.items.map((item: MegaMenuItem) => {
                          const isExternal = item.isExternal || item.path?.startsWith('http');

                          const content = (
                            <>
                              <div className="flex items-center gap-3">
                                {idx === 0 ? (
                                  <div className="text-xl font-black text-slate-900 dark:text-[#EDF0F1] group-hover:text-[#0E93A3] transition-all duration-300 tracking-tight leading-tight">
                                    {item.title}
                                  </div>
                                ) : (
                                  <div className="text-sm font-bold text-slate-700 dark:text-slate-200 group-hover:text-[#0E93A3] transition-colors flex items-center gap-2">
                                    {item.title}
                                    {isExternal && (
                                      <ArrowRight
                                        size={12}
                                        className={`opacity-0 ${
                                          lang === 'ar' ? 'translate-x-2 rotate-180' : '-translate-x-2'
                                        } group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#0E93A3]`}
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                              {item.desc && (
                                <p className={`${idx === 0 ? 'text-xs mt-1' : 'text-[11px] mt-0.5'} text-slate-500 dark:text-[#9AA5AC] font-medium leading-relaxed group-hover:text-slate-900 dark:group-hover:text-[#EDF0F1] transition-colors`}>
                                  {item.desc}
                                </p>
                              )}
                            </>
                          );

                          const handleItemClick = () => {
                            if (onClose) {
                              onClose();
                            } else {
                              setHoveredPath(null);
                            }
                          };

                          if (isExternal) {
                            return (
                              <a
                                key={item.id || `${item.path}-${item.title}`}
                                href={item.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={handleItemClick}
                                className="group block"
                              >
                                {content}
                              </a>
                            );
                          }

                          return (
                            <Link
                              key={item.id || `${item.path}-${item.title}`}
                              to={getLocalizedPath(item.path, lang)}
                              onClick={handleItemClick}
                              className="group block"
                            >
                              {content}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
